import dayjs, { Dayjs } from 'dayjs/esm/index';
import customParseFormat from 'dayjs/esm/plugin/customParseFormat';

dayjs.extend(customParseFormat);

/**
 * A native, dependency-free port of `bootstrap-daterangepicker@1.3.2-1.1` — the last thing keeping
 * jQuery in the account app (it declared jquery + moment as HARD dependencies and `require()`d both
 * at module scope, so neither could leave `package.json` while it stayed).
 *
 * WHY A 1:1 DOM PORT RATHER THAN NgbDatepicker
 * The opened calendar's entire appearance comes from a stylesheet this repo VENDORS —
 * `src/account-v1/assets/css/vendor/daterangepicker-bs3.css` (335 lines, first in angular.json's
 * styles array) plus ~250 lines of Flip.to overrides in `account-v2/src/assets/styles/style.css`.
 * The npm package ships no CSS that we load. Emitting the same element/class structure therefore
 * inherits the exact pixels for free, which matters more than usual here because
 * `account-parity/src/manifest/routes.ts:3743` says the gate captures these surfaces with the picker
 * CLOSED — "the jQuery calendar (opened) is gate-blind". An NgbDatepicker would emit `.ngb-dp-*`
 * markup that none of that CSS targets, i.e. a visual change with no gate to catch it.
 *
 * Two more reasons this is a plain class and not an Angular component: the calendar is appended to
 * <body> (the modals it opens inside are `position:fixed; overflow-y:auto` scroll containers that
 * would clip a ~790px-wide inline dropdown), and the vendored CSS's `z-index: 1000000` is a
 * deliberate +1 over the app-global `.cdk-overlay-container { z-index: 999999 }`
 * (`libs/shared-assets/styles/material/_mat-dialog.scss:1-3`) — that is the only reason the calendar
 * paints above the modal. Routing this through a CDK overlay would ALSO have to out-specify the
 * global `.cdk-overlay-pane { position: relative !important; width: 100%; height: 100% }`, the trap
 * already documented at `ft-filter-popover.component.scss:16-32` ("it rendered at viewport 0,0 while
 * its trigger sat at x:1022").
 *
 * DELIBERATE DIVERGENCES FROM THE PLUGIN (everything else is verbatim)
 * 1. dayjs is IMMUTABLE where moment was not. The plugin handed consumers its own live internal
 *    moment objects and kept mutating them; callbacks here receive stable snapshots.
 * 2. `yearless` mode (the pre-stay season editor). The plugin was handed 'March 14'-style strings
 *    with NO YEAR and parsed them with `moment(str, 'MMMM D')`, which silently resolves to the
 *    CURRENT year. Two live-data consequences, both fixed here under `yearless`:
 *      - Feb 29 was INVALID in every non-leap year (1 live row): the calendar rendered 42 cells of
 *        'NaN' and a header reading 'undefinedInvalid date'. We pin {@link PINNED_YEAR} = 2024, a
 *        LEAP year, so Feb 29 is selectable and round-trips.
 *      - A season that wraps New Year (9 live rows, e.g. Oct 11 -> Apr 30) parsed to start > end,
 *        which disabled Apply, rendered the whole right calendar unclickable, and — on the next
 *        click — made `setCustomDates` reuse the stored NEGATIVE duration to derive a fresh, still
 *        incoherent end date. Range membership here is computed on a WRAPPING month/day ordinal
 *        instead, and picking a new start leaves the end alone.
 *    The persisted contract is unchanged either way: callers read `.month()`/`.date()` and store
 *    {startMonth, startDay, endMonth, endDay}. The pinned year is never emitted as a year.
 * 3. `showYearInHeader` / `showFullMonthInHeader` / `showDaysOfWeek` are NOT implemented, exactly as
 *    before: they appear nowhere in the plugin (grep exit 1) and no CSS achieved them either, so
 *    they were always dead. Header rendering is unchanged (abbreviated month + year + weekday row).
 *    Whether that SHOULD change is a design question raised separately, not smuggled in here.
 */

/** Leap year, so Feb 29 is reachable in `yearless` mode. See divergence (2) above. */
export const PINNED_YEAR = 2024;

export interface FtDaterangeLocale {
    applyLabel: string;
    cancelLabel: string;
    fromLabel: string;
    toLabel: string;
    daysOfWeek: string[];
    monthNames: string[];
    firstDay: number;
}

export interface FtDaterangeOptions {
    startDate?: Dayjs | Date | string;
    endDate?: Dayjs | Date | string;
    minDate?: Dayjs | Date | string;
    maxDate?: Dayjs | Date | string;
    format?: string;
    separator?: string;
    singleDatePicker?: boolean;
    opens?: 'left' | 'right' | 'center';
    drops?: 'up' | 'down';
    /** Month/day-only season range: pin {@link PINNED_YEAR}, wrap-aware ranges, year never emitted. */
    yearless?: boolean;
    locale?: Partial<FtDaterangeLocale>;
    /** Read by FtDateRangeDirective (moments vs native Dates out), not by the calendar itself. */
    convertToNative?: boolean;
    [key: string]: unknown;
}

export type FtDaterangeCallback = (start: Dayjs, end: Dayjs) => void;

/** v1 defaults — `moment.weekdaysMin()` / `moment.monthsShort()` / `_week.dow` for the `en` locale. */
const DEFAULT_LOCALE: FtDaterangeLocale = {
    applyLabel: 'Apply',
    cancelLabel: 'Cancel',
    fromLabel: 'From',
    toLabel: 'To',
    daysOfWeek: ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'],
    monthNames: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    firstDay: 0,
};

const TEMPLATE =
    '<div class="daterangepicker dropdown-menu">' +
    '<div class="calendar first left"></div>' +
    '<div class="calendar second right"></div>' +
    '<div class="ranges">' +
    '<div class="range_inputs">' +
    '<div class="daterangepicker_start_input">' +
    '<label for="daterangepicker_start"></label>' +
    '<input class="input-mini" type="text" name="daterangepicker_start" value="" />' +
    '</div>' +
    '<div class="daterangepicker_end_input">' +
    '<label for="daterangepicker_end"></label>' +
    '<input class="input-mini" type="text" name="daterangepicker_end" value="" />' +
    '</div>' +
    '<button class="applyBtn" disabled="disabled"></button>&nbsp;' +
    '<button class="cancelBtn"></button>' +
    '</div>' +
    '</div>' +
    '</div>';

/** Month/day as a comparable ordinal within the pinned leap year — the basis of wrap-aware ranges. */
const ordinal = (d: Dayjs): number => d.month() * 100 + d.date();

export class FtDaterangeCalendar {
    startDate!: Dayjs;
    endDate!: Dayjs;
    minDate: Dayjs | null = null;
    maxDate: Dayjs | null = null;

    format = 'MM/DD/YYYY';
    separator = ' - ';
    singleDatePicker = false;
    opens: 'left' | 'right' | 'center' = 'right';
    drops: 'up' | 'down' = 'down';
    yearless = false;
    locale: FtDaterangeLocale = { ...DEFAULT_LOCALE };

    readonly container: HTMLElement;
    private isShowing = false;
    private cb: FtDaterangeCallback | null = null;
    private oldStartDate!: Dayjs;
    private oldEndDate!: Dayjs;
    private leftMonth!: Dayjs;
    private rightMonth!: Dayjs;
    private leftGrid: Dayjs[][] = [];
    private rightGrid: Dayjs[][] = [];

    /**
     * v1 bound `mousedown.daterangepicker` on document (js:626-635), NOT click. The ordering is
     * load-bearing for correctness: the pre-stay edit host carries a SIBLING
     * `(click)="selectDateRange(dateRange)"`, so when the user moves from open dropdown A to
     * dropdown B, A's mousedown-triggered hide must fire its callback while `selectedDateRange` is
     * still A. Closing on `click` instead reverses that and writes A's dates onto row B.
     */
    private readonly onDocumentMouseDown = (e: MouseEvent) => {
        const target = e.target as Node;
        if (this.container.contains(target) || this.element.contains(target) || this.element === target) return;
        this.hide();
    };

    constructor(
        private readonly element: HTMLElement,
        options: FtDaterangeOptions = {},
        cb?: FtDaterangeCallback,
    ) {
        const holder = document.createElement('div');
        holder.innerHTML = TEMPLATE;
        this.container = holder.firstElementChild as HTMLElement;
        document.body.appendChild(this.container);
        this.container.style.display = 'none';

        this.container.addEventListener('click', this.onContainerClick);
        this.element.addEventListener('click', this.onElementClick);

        this.setOptions(options, cb);
    }

    // ── options ─────────────────────────────────────────────────────────────────────────────────
    /** v1 setOptions (js:113-455): format FIRST, then dates, then chrome. Order matters. */
    setOptions(options: FtDaterangeOptions = {}, cb?: FtDaterangeCallback): void {
        if (typeof options.format === 'string') this.format = options.format;
        if (typeof options.separator === 'string') this.separator = options.separator;
        // `yearless` is INFERRED from the format when not stated: a format carrying no year token
        // cannot round-trip one, so the dates it describes are month/day only. That is exactly the
        // pre-stay season editor's `format: 'MMMM D'`, and it means the two call sites need no new
        // flag. An explicit `yearless` always wins.
        this.yearless = typeof options.yearless === 'boolean'
            ? options.yearless
            : !/Y/.test(typeof options.format === 'string' ? options.format : this.format);
        if (options.locale) this.locale = { ...DEFAULT_LOCALE, ...options.locale };
        if (typeof options.singleDatePicker === 'boolean') this.singleDatePicker = options.singleDatePicker;
        if (options.opens === 'left' || options.opens === 'right' || options.opens === 'center') this.opens = options.opens;
        if (options.drops === 'up' || options.drops === 'down') this.drops = options.drops;

        // v1 default: today 00:00 -> today 23:59:59.999 (js:115-116). The pre-stay ADD trigger
        // passes no dates at all and relies on exactly this.
        const today = this.yearless ? dayjs().year(PINNED_YEAR) : dayjs();
        this.startDate = this.parse(options.startDate) ?? today.startOf('day');
        this.endDate = this.parse(options.endDate) ?? today.endOf('day');
        this.minDate = this.parse(options.minDate);
        this.maxDate = this.parse(options.maxDate);

        // v1 js:262-267 — singleDatePicker collapses the range to a point.
        if (this.singleDatePicker) this.endDate = this.startDate.clone();

        this.startDate = this.startDate.startOf('day');
        this.endDate = this.endDate.endOf('day');

        if (cb) this.cb = cb;

        this.oldStartDate = this.startDate.clone();
        this.oldEndDate = this.endDate.clone();
        this.leftMonth = this.startDate.startOf('month');
        this.rightMonth = this.endDate.startOf('month');

        this.applyChrome();
        this.updateView();
    }

    /**
     * v1 js:166-188 — STRING dates parse with the configured `format`, everything else directly.
     * Under `yearless` the year is forced to {@link PINNED_YEAR}, which is what makes 'February 29'
     * parse at all (it is `Invalid date` in any non-leap year, and the plugin had no isValid guard).
     */
    private parse(value: Dayjs | Date | string | undefined | null): Dayjs | null {
        if (value === undefined || value === null || value === '') return null;
        let d: Dayjs;
        if (typeof value === 'string') {
            // NEVER `dayjs(str)` bare: the plugin's own fallback for an unformatted 'March 14' went
            // through `new Date(str)`, which V8 resolves to the year 2001 — a 25-year silent drift.
            d = this.yearless
                ? dayjs(`${value} ${PINNED_YEAR}`, `${this.format} YYYY`, true)
                : dayjs(value, this.format);
        } else {
            d = dayjs(value as Date);
        }
        if (this.yearless && d.isValid()) d = d.year(PINNED_YEAR);
        return d.isValid() ? d : null;
    }

    // ── chrome ──────────────────────────────────────────────────────────────────────────────────
    /** v1 js:382-454 — single-mode collapse, the opens-based calendar swap, labels, button classes. */
    private applyChrome(): void {
        const first = this.container.querySelector('.calendar.first') as HTMLElement;
        const second = this.container.querySelector('.calendar.second') as HTMLElement;
        const ranges = this.container.querySelector('.ranges') as HTMLElement;

        if (this.singleDatePicker) {
            // v1 forces opens='right' in single mode (js:383) — which then triggers the swap below.
            this.opens = 'right';
            this.container.classList.add('single');
            second.classList.add('single');
            ranges.style.display = 'none';
        } else {
            this.container.classList.remove('single');
            second.classList.remove('single');
            ranges.style.display = '';
        }

        // v1 js:414-431 — for opens right/center the two calendars SWAP their left/right classes,
        // so `.calendar.first` ends up classed `right`. updateCalendars() keys off the CLASS, not
        // the ordinal, so this must be reproduced or the two months render transposed.
        if (this.opens === 'right' || this.opens === 'center') {
            if (second.classList.contains('single')) {
                second.classList.remove('single');
                first.classList.add('single');
            }
            first.classList.remove('left');
            first.classList.add('right');
            second.classList.remove('right');
            second.classList.add('left');
            if (this.singleDatePicker) {
                // MUST be an explicit inline 'block', not ''. The vendored sheet hides calendars by
                // default — `.daterangepicker .calendar { display: none }` — and lifts that ONLY via
                // `.daterangepicker.show-calendar .calendar` (daterangepicker-bs3.css:43,48). v1 never
                // adds `show-calendar` in single mode (js:433 excludes it); it relied on jQuery
                // `.show()` (js:427-429) writing an inline display that out-ranks the stylesheet.
                // Clearing the property instead leaves the sheet's `display:none` winning, which
                // collapses the whole picker to ~10x20px with its cells present but invisible.
                first.style.display = 'block';
                second.style.display = 'none';
            }
        }

        // ALWAYS, in both modes. js:433 only adds this for range mode, which reads like single mode
        // should go without — but updateView()'s customRange branch (js:976-979) then calls
        // showCalendars(), which adds it too, and `customRange` is always true when no `ranges`
        // option is supplied (none of our call sites supplies one). Measured on the real jQuery
        // build: the open single-date picker carries `show-calendar`. Omitting it here left the
        // vendored `.daterangepicker .calendar { display: none }` in force and collapsed the picker.
        this.container.classList.add('show-calendar');
        this.container.classList.remove('opensleft', 'opensright', 'openscenter');
        this.container.classList.add('opens' + this.opens);

        // v1 buttonClasses ['btn','btn-small btn-sm'] + applyClass/cancelClass (js:139-141, 442-454).
        for (const btn of Array.from(this.container.querySelectorAll('button'))) {
            btn.classList.add('btn', 'btn-small', 'btn-sm');
        }
        this.container.querySelector('.applyBtn')!.classList.add('btn-success');
        this.container.querySelector('.cancelBtn')!.classList.add('btn-default');
        this.container.querySelector('.daterangepicker_start_input label')!.textContent = this.locale.fromLabel;
        this.container.querySelector('.daterangepicker_end_input label')!.textContent = this.locale.toLabel;
        this.container.querySelector('.applyBtn')!.textContent = this.locale.applyLabel;
        this.container.querySelector('.cancelBtn')!.textContent = this.locale.cancelLabel;
    }

    // ── rendering ───────────────────────────────────────────────────────────────────────────────
    private updateView(): void {
        this.leftGrid = this.buildCalendar(this.leftMonth);
        this.rightGrid = this.buildCalendar(this.rightMonth);
        const left = this.container.querySelector('.calendar.left') as HTMLElement;
        const right = this.container.querySelector('.calendar.right') as HTMLElement;
        // v1 js:953-954 — the right calendar's floor is the START date in range mode.
        if (left) left.innerHTML = this.renderCalendar(this.leftGrid, this.startDate, this.minDate, this.maxDate);
        if (right) {
            right.innerHTML = this.renderCalendar(
                this.rightGrid, this.endDate, this.singleDatePicker ? this.minDate : this.rangeFloor(), this.maxDate,
            );
        }
        this.updateFormInputs();
    }

    /**
     * The right calendar's minimum. v1 used `this.startDate` unconditionally, which is what made a
     * wrapped season's right calendar 100% unclickable (every cell fell before the start and picked
     * up ' off disabled ', replacing 'available'; the delegated handler only listens on td.available).
     * Under `yearless` a wrap is legitimate, so there is no floor.
     */
    private rangeFloor(): Dayjs | null {
        if (this.yearless && ordinal(this.endDate) < ordinal(this.startDate)) return null;
        return this.startDate;
    }

    /** v1 buildCalendar (js:982-1022) — 6x7 grid anchored on the week containing the 1st. */
    private buildCalendar(month: Dayjs): Dayjs[][] {
        const firstDay = month.startOf('month');
        const daysInLastMonth = firstDay.subtract(1, 'month').daysInMonth();
        const dayOfWeek = firstDay.day();

        let startDay = daysInLastMonth - dayOfWeek + this.locale.firstDay + 1;
        if (startDay > daysInLastMonth) startDay -= 7;
        if (dayOfWeek === this.locale.firstDay) startDay = daysInLastMonth - 6;

        // v1 walks from noon in 24h steps so a DST transition cannot shift a cell onto the wrong day.
        let cur = firstDay.subtract(1, 'month').date(startDay).hour(12);
        const grid: Dayjs[][] = [[], [], [], [], [], []];
        for (let i = 0, col = 0, row = 0; i < 42; i++, col++) {
            if (i > 0 && col % 7 === 0) { col = 0; row++; }
            grid[row][col] = cur.startOf('day');
            cur = cur.add(24, 'hour').hour(12);
        }
        return grid;
    }

    /** True when `d` falls inside the selection, wrapping the year end under `yearless`. */
    private inRange(d: Dayjs): boolean {
        if (this.yearless) {
            const o = ordinal(d);
            const s = ordinal(this.startDate);
            const e = ordinal(this.endDate);
            return s <= e ? o >= s && o <= e : o >= s || o <= e;
        }
        return !d.isBefore(this.startDate, 'day') && !d.isAfter(this.endDate, 'day');
    }

    /** v1 renderCalendar (js:1068-1148) — same elements, same class rules, same `data-title`. */
    private renderCalendar(grid: Dayjs[][], selected: Dayjs, minDate: Dayjs | null, maxDate: Dayjs | null): string {
        const anchor = grid[1][1];
        const firstDay = anchor.startOf('month');
        const lastDay = anchor.endOf('month');
        const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]!));

        let html = '<div class="calendar-date"><table class="table-condensed"><thead><tr>';
        html += (!minDate || minDate.isBefore(firstDay))
            ? '<th class="prev available"><i class="fa fa-arrow-left icon icon-arrow-left glyphicon glyphicon-arrow-left"></i></th>'
            : '<th></th>';
        // v1 js:1085 hardcodes abbreviated-month + ' YYYY'. Under yearless the pinned year would be
        // a lie, so the year is dropped there — and ONLY there.
        const headline = this.yearless
            ? this.locale.monthNames[anchor.month()]
            : this.locale.monthNames[anchor.month()] + anchor.format(' YYYY');
        html += '<th colspan="5" class="month">' + esc(headline) + '</th>';
        html += (!maxDate || maxDate.isAfter(lastDay))
            ? '<th class="next available"><i class="fa fa-arrow-right icon icon-arrow-right glyphicon glyphicon-arrow-right"></i></th>'
            : '<th></th>';
        html += '</tr><tr>';
        for (const d of this.locale.daysOfWeek) html += '<th>' + esc(d) + '</th>';
        html += '</tr></thead><tbody>';

        for (let row = 0; row < 6; row++) {
            html += '<tr>';
            for (let col = 0; col < 7; col++) {
                const cell = grid[row][col];
                let cname = 'available ';
                cname += cell.month() === anchor.month() ? '' : 'off';
                if ((minDate && cell.isBefore(minDate, 'day')) || (maxDate && cell.isAfter(maxDate, 'day'))) {
                    // v1 js:1125 REPLACES the class list here — the cell stops being `available`,
                    // which is precisely what makes it unclickable.
                    cname = ' off disabled ';
                } else if (cell.isSame(selected, 'day')) {
                    cname += ' active ';
                    if (cell.isSame(this.startDate, 'day')) cname += ' start-date ';
                    if (cell.isSame(this.endDate, 'day')) cname += ' end-date ';
                } else if (this.inRange(cell)) {
                    cname += ' in-range ';
                    if (cell.isSame(this.startDate, 'day')) cname += ' start-date ';
                    if (cell.isSame(this.endDate, 'day')) cname += ' end-date ';
                }
                cname = cname.replace(/\s+/g, ' ').trim();
                html += '<td class="' + cname + '" data-title="r' + row + 'c' + col + '">' + cell.date() + '</td>';
            }
            html += '</tr>';
        }
        return html + '</tbody></table></div>';
    }

    /** v1 js:497-506 — mirror the dates into the mini inputs and gate Apply on start <= end. */
    private updateFormInputs(): void {
        const s = this.container.querySelector('input[name=daterangepicker_start]') as HTMLInputElement;
        const e = this.container.querySelector('input[name=daterangepicker_end]') as HTMLInputElement;
        if (s) s.value = this.startDate.format(this.format);
        if (e) e.value = this.endDate.format(this.format);
        const apply = this.container.querySelector('.applyBtn') as HTMLButtonElement;
        if (!apply) return;
        // Under yearless a wrapped range is legitimate, so Apply stays enabled — v1 disabled it,
        // stranding 9 live rows.
        const ok = this.yearless || !this.startDate.isAfter(this.endDate);
        if (ok) apply.removeAttribute('disabled');
        else apply.setAttribute('disabled', 'disabled');
    }

    // ── interaction ─────────────────────────────────────────────────────────────────────────────
    private readonly onContainerClick = (e: MouseEvent) => {
        const t = e.target as HTMLElement;
        const cal = t.closest('.calendar');
        if (t.closest('th.prev.available') && cal) return this.shiftMonth(cal, -1);
        if (t.closest('th.next.available') && cal) return this.shiftMonth(cal, 1);
        const td = t.closest('td.available') as HTMLElement | null;
        if (td && cal && !td.classList.contains('disabled')) return this.clickDate(td, cal);
        if (t.closest('.applyBtn')) return this.clickApply();
        if (t.closest('.cancelBtn')) return this.clickCancel();
    };

    private readonly onElementClick = () => (this.isShowing ? this.hide() : this.show());

    private shiftMonth(cal: Element, delta: number): void {
        if (cal.classList.contains('left')) this.leftMonth = this.leftMonth.add(delta, 'month');
        else this.rightMonth = this.rightMonth.add(delta, 'month');
        this.updateView();
    }

    /** v1 clickDate (js:808-852) — left sets start, right sets end; single applies immediately. */
    private clickDate(td: HTMLElement, cal: Element): void {
        const title = td.getAttribute('data-title') || '';
        const row = +title.substr(1, 1);
        const col = +title.substr(3, 1);
        const isLeft = cal.classList.contains('left');
        let start = this.startDate;
        let end = this.endDate;
        if (isLeft) start = this.leftGrid[row][col];
        else end = this.rightGrid[row][col];

        if (this.singleDatePicker) {
            start = isLeft ? this.leftGrid[row][col] : this.rightGrid[row][col];
            end = start.clone();
        }

        this.setCustomDates(start, end);
        if (this.singleDatePicker) this.clickApply();
    }

    /**
     * v1 setCustomDates (js:792-806) minus its duration-preserving branch.
     *
     * That branch fired whenever start ended up after end, and reused `endDate.diff(startDate)` —
     * NEGATIVE for a wrapped season — so clicking Oct 20 on an Oct 11 -> Apr 30 range derived an end
     * of May 9 and wrote back another incoherent range. Here a wrap is either legitimate (yearless)
     * or clamped by the right calendar's floor, so the end is simply left alone.
     */
    private setCustomDates(start: Dayjs, end: Dayjs): void {
        this.startDate = start.startOf('day');
        this.endDate = end.endOf('day');
        this.leftMonth = this.startDate.startOf('month');
        this.rightMonth = this.endDate.startOf('month');
        this.updateView();
    }

    private clickApply(): void {
        this.hide();
    }

    private clickCancel(): void {
        this.startDate = this.oldStartDate;
        this.endDate = this.oldEndDate;
        this.updateView();
        this.hide();
    }

    // ── visibility ──────────────────────────────────────────────────────────────────────────────
    show(): void {
        if (this.isShowing) return;
        this.oldStartDate = this.startDate.clone();
        this.oldEndDate = this.endDate.clone();
        this.container.style.display = 'block';
        this.element.classList.add('active');
        this.isShowing = true;
        this.move();
        // Registered on the NEXT frame so the click that opened the picker cannot immediately close
        // it, and as mousedown to preserve the v1 ordering (see onDocumentMouseDown).
        setTimeout(() => document.addEventListener('mousedown', this.onDocumentMouseDown), 0);
    }

    hide(): void {
        if (!this.isShowing) return;
        document.removeEventListener('mousedown', this.onDocumentMouseDown);
        this.element.classList.remove('active');
        this.container.style.display = 'none';
        this.isShowing = false;
        // v1 js:664-665 — notify ONLY when the dates actually moved.
        if (!this.startDate.isSame(this.oldStartDate) || !this.endDate.isSame(this.oldEndDate)) this.notify();
        this.oldStartDate = this.startDate.clone();
        this.oldEndDate = this.endDate.clone();
    }

    private notify(): void {
        this.updateView();
        if (this.cb) this.cb(this.startDate, this.endDate);
    }

    /** v1 move (js:552-608) — same anchor maths, against the document rather than jQuery offsets. */
    private move(): void {
        const rect = this.element.getBoundingClientRect();
        const scrollX = window.pageXOffset;
        const scrollY = window.pageYOffset;
        const style = this.container.style;
        style.top = '';
        style.left = '';
        style.right = '';

        const top = this.drops === 'up'
            ? rect.top + scrollY - this.container.offsetHeight
            : rect.bottom + scrollY;
        this.container.classList.toggle('dropup', this.drops === 'up');
        style.top = top + 'px';

        if (this.opens === 'left') {
            style.right = (document.documentElement.clientWidth - rect.right) + 'px';
            style.left = 'auto';
            if (this.container.getBoundingClientRect().left < 0) {
                style.right = 'auto';
                style.left = '9px';
            }
        } else if (this.opens === 'center') {
            style.left = (rect.left + scrollX + rect.width / 2 - this.container.offsetWidth / 2) + 'px';
            style.right = 'auto';
            if (this.container.getBoundingClientRect().left < 0) {
                style.right = 'auto';
                style.left = '9px';
            }
        } else {
            style.left = (rect.left + scrollX) + 'px';
            style.right = 'auto';
            if (this.container.getBoundingClientRect().right > document.documentElement.clientWidth) {
                style.left = 'auto';
                style.right = '0';
            }
        }
    }

    /** v1 `.remove()` — drop the body-appended node and every listener. */
    remove(): void {
        document.removeEventListener('mousedown', this.onDocumentMouseDown);
        this.element.removeEventListener('click', this.onElementClick);
        this.container.removeEventListener('click', this.onContainerClick);
        this.container.remove();
    }
}
